import React from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useCalculateSplit, useCreatePaymentIntent } from '../../hooks/useLedger';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../../contexts/TenantContext';
import { SplitBreakdown } from './SplitBreakdown';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { CheckCircle2, CreditCard, AlertCircle, Info, ShieldAlert } from 'lucide-react';
import debounce from 'lodash.debounce';
import toast from 'react-hot-toast';

const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

interface PaymentFormContentProps {
  nodeId: string;
}

const PaymentFormContent: React.FC<PaymentFormContentProps> = ({ nodeId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const { node } = useTenant();
  
  const [amount, setAmount] = React.useState<number>(0);
  const [displayAmount, setDisplayAmount] = React.useState<string>('');
  const [description, setDescription] = React.useState('');
  const [isAnonymous, setIsAnonymous] = React.useState(false);
  const [onBehalfOfNotes, setOnBehalfOfNotes] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [paymentSuccess, setPaymentSuccess] = React.useState<{ id: string; amount: number } | null>(null);
  const [showCardElement, setShowCardElement] = React.useState(false);

  const { data: splits = [], isLoading: isCalculating } = useCalculateSplit(nodeId, amount);
  const createIntentMutation = useCreatePaymentIntent(nodeId);

  const debouncedSetAmount = React.useMemo(
    () => debounce((val: number) => setAmount(val), 300),
    []
  );

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setDisplayAmount(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      debouncedSetAmount(num);
    } else {
      setAmount(0);
    }
  };

  const handleInitiatePayment = async () => {
    if (amount <= 0) return;
    setShowCardElement(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || amount <= 0) return;

    setIsProcessing(true);

    try {
      // 1. Create Payment Intent
      const { clientSecret } = await createIntentMutation.mutateAsync({
        amount,
        description,
        isAnonymous,
        onBehalfOfNotes
      });

      // 2. Confirm Payment
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card element not found');

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement as any,
          billing_details: {
            name: isAnonymous ? 'Anonymous Benefactor' : 'Member',
          },
        },
      });

      if (result.error) {
        toast.error(result.error.message || 'Payment failed');
      } else {
        if (result.paymentIntent.status === 'succeeded') {
          setPaymentSuccess({
            id: result.paymentIntent.id,
            amount: result.paymentIntent.amount / 100
          });
          toast.success('Payment successful!');
          queryClient.invalidateQueries({ queryKey: ['ledger-transactions', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['ledger-stats', nodeId] });
          queryClient.invalidateQueries({ queryKey: ['ledger-stats-me', nodeId] });
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      toast.error('An error occurred during payment');
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <Card className="text-center py-8 space-y-6">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Payment Successful</h2>
          <p className="text-sm text-gray-500">
            {isAnonymous 
              ? 'Thank you! Your payment has been processed anonymously.' 
              : 'Thank you for your contribution.'}
          </p>
        </div>

        <div className="max-w-xs mx-auto bg-gray-50 rounded-xl p-6 border border-gray-200 text-left dark:bg-slate-950">
          <div className="flex items-center justify-between mb-4">
            <Badge variant="green">Paid</Badge>
            <span className="text-[10px] font-mono text-gray-400 uppercase">Receipt</span>
          </div>

          <div className="mb-4 space-y-1">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Payer</p>
            <p className="text-xs font-semibold text-gray-900 dark:text-white">
              {isAnonymous ? 'Anonymous Benefactor' : 'Member'}
            </p>
          </div>

          {!isAnonymous && onBehalfOfNotes && (
            <div className="mb-4 space-y-1">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono">Paid On Behalf Of</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 italic">{onBehalfOfNotes}</p>
            </div>
          )}

          {isAnonymous && onBehalfOfNotes && (
            <div className="mb-4 p-2 bg-blue-50/50 rounded-lg border border-blue-100/50 text-[11px] text-blue-700 leading-relaxed dark:bg-slate-900/50 dark:border-blue-900/50 dark:text-blue-300">
              <ShieldAlert size={12} className="inline mr-1 -mt-0.5 text-blue-600 dark:text-blue-400" />
              Beneficiary name has been masked on this receipt to preserve anonymity.
            </div>
          )}
          
          <SplitBreakdown splits={splits} />
          
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-1">Transaction ID</p>
            <p className="text-[10px] font-mono text-gray-500 break-all">{paymentSuccess.id}</p>
          </div>
        </div>

        <Button variant="secondary" onClick={() => {
          setPaymentSuccess(null);
          setDisplayAmount('');
          setAmount(0);
          setDescription('');
          setIsAnonymous(false);
          setOnBehalfOfNotes('');
          setShowCardElement(false);
        }}>
          Make another payment
        </Button>
      </Card>
    );
  }


  if (node && node.id === nodeId && !node.chargesEnabled && !node.stripeAccountId) {
    return (
      <Card className="space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gray-100 text-gray-500 rounded-lg dark:bg-slate-800">
            <CreditCard size={20} />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Make a Payment</h3>
        </div>
        <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-amber-800 text-sm flex gap-3 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200">
          <AlertCircle size={20} className="shrink-0 mt-0.5" />
          <p>This organization is currently not accepting digital payments.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-50 text-blue-700 rounded-lg">
            <CreditCard size={20} />
          </div>
          <h3 className="font-semibold text-gray-900 dark:text-white">Make a Payment</h3>
        </div>

        <div className="p-4 bg-brand-primary/5 border border-brand-primary/10 rounded-xl mb-2">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-brand-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-xs font-bold text-gray-900 dark:text-gray-100">Why pay online instead of a check?</h4>
              <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-relaxed">
                Paying online instantly updates your standing to "Paid". It also saves our volunteer Treasurer from hours of manual spreadsheet work, trips to the bank, and chasing down late payments.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input 
            label="Amount ($)" 
            type="number" 
            step="0.01"
            placeholder="0.00"
            value={displayAmount}
            onChange={handleAmountChange}
            disabled={isProcessing}
          />
          <Input 
            label="Description" 
            placeholder="e.g. Monthly Dues"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isProcessing}
          />
        </div>

        <div className="space-y-4 border-t border-gray-150 pt-4 dark:border-slate-800">
          <Input 
            label="Paying on behalf of (Optional)" 
            placeholder="e.g. Timmy Avery, or the entire troop"
            value={onBehalfOfNotes}
            onChange={(e) => setOnBehalfOfNotes(e.target.value)}
            disabled={isProcessing}
          />
          
          <label className="flex items-start gap-3 cursor-pointer group p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-900/50 transition-colors border border-transparent hover:border-gray-100 dark:hover:border-slate-850">
            <input 
              type="checkbox" 
              checked={isAnonymous} 
              onChange={(e) => setIsAnonymous(e.target.checked)}
              disabled={isProcessing}
              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 dark:bg-slate-800 dark:border-slate-700"
            />
            <div className="space-y-1">
              <span className="text-xs font-bold text-gray-900 dark:text-gray-100 block group-hover:text-blue-650 dark:group-hover:text-blue-400 transition-colors">Make this payment anonymous</span>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed block">
                Your name will be masked as "Anonymous Benefactor" on public ledger feeds. To prevent bragging, beneficiary names are also masked on your receipt.
              </span>
            </div>
          </label>

          {isAnonymous && (
            <div className="p-3 bg-blue-50 text-blue-800 rounded-xl border border-blue-100 text-xs flex gap-2.5 animate-in fade-in slide-in-from-top-1 duration-200 dark:bg-slate-950 dark:border-blue-950 dark:text-blue-300">
              <ShieldAlert size={16} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold mb-0.5 text-blue-900 dark:text-blue-200">Anonymity Note</h4>
                <p className="text-[11px] leading-relaxed">
                  While masked in transaction histories, this payment remains securely linked to your account for auditing, tax receipts, and payment dispute resolutions.
                </p>
              </div>
            </div>
          )}
        </div>


        {amount > 0 && (
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 animate-in fade-in slide-in-from-top-2 duration-200 dark:bg-slate-950">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-4">Fee Split Preview</p>
            {isCalculating ? (
              <div className="flex items-center justify-center py-4">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <SplitBreakdown splits={splits} />
            )}
          </div>
        )}

        {!showCardElement ? (
          <Button 
            className="w-full" 
            disabled={amount <= 0 || isCalculating}
            onClick={handleInitiatePayment}
          >
            Pay ${amount > 0 ? amount.toFixed(2) : '0.00'}
          </Button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-4 bg-white border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-500 transition-all dark:bg-slate-900">
              <CardElement 
                options={{
                  style: {
                    base: {
                      fontSize: '14px',
                      color: '#111827',
                      '::placeholder': {
                        color: '#9CA3AF',
                      },
                    },
                  },
                }}
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100 text-amber-800 text-xs">
              <AlertCircle size={14} className="flex-shrink-0" />
              <p>Your payment will be split across the organization hierarchy as shown above.</p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="secondary" 
                className="flex-1" 
                onClick={() => setShowCardElement(false)}
                disabled={isProcessing}
              >
                Back
              </Button>
              <Button 
                type="submit" 
                className="flex-[2]" 
                isLoading={isProcessing}
                disabled={!stripe || isProcessing}
              >
                Confirm Payment
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
};

export const PaymentForm: React.FC<PaymentFormContentProps> = ({ nodeId }) => {
  if (!stripePromise) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-amber-600 dark:text-amber-400">
          <AlertCircle size={20} />
          <p className="text-sm font-medium">Online payments are currently unconfigured.</p>
        </div>
      </Card>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentFormContent nodeId={nodeId} />
    </Elements>
  );
};
